import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import episodesLocal from "../data/episodes";

const API_BASE = "https://elshowdelasbanqueras.com/show-banqueras-api";

const EPISODES_LIST_URL = `${API_BASE}/api/episodes_list.php?status=approved`;
const SITE_URL = "https://elshowdelasbanqueras.com"; // ✅ cambia si tu URL final es otra
const PAGE_URL = `${SITE_URL}/`; // home
const OG_IMAGE =
  "https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=1200&q=60";

const KEYWORDS =
  "resultados de loterías, banqueras, bancas de loterias, agente de ventas, casinos, empleada de casinos, maquinas tragas monedas, team banqueras, las banqueras activas, el show de las banqueras, las banqueras de republica dominicana";

const FALLBACK_IMG = OG_IMAGE;

export default function Home() {
  const [episodes, setEpisodes] = useState([]);
  const [loadingEp, setLoadingEp] = useState(true);
  const [epErr, setEpErr] = useState("");

  const safeJson = async (res, label) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`La API no devolvió JSON válido (${label}).`);
    }
  };

  const fetchEpisodes = async () => {
    setLoadingEp(true);
    setEpErr("");

    try {
      const res = await fetch(EPISODES_LIST_URL);
      const data = await safeJson(res, "episodes_list.php");
      if (!res.ok) throw new Error(data?.message || "Error cargando episodios");

      // esperamos array
      const rows = Array.isArray(data) ? data : [];
      setEpisodes(rows);
    } catch (e) {
      console.warn("Episodes API error:", e?.message);
      setEpErr(e.message || "No se pudieron cargar episodios del servidor.");
      setEpisodes(Array.isArray(episodesLocal) ? episodesLocal : []);
    } finally {
      setLoadingEp(false);
    }
  };

  useEffect(() => {
    fetchEpisodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = useMemo(() => (Array.isArray(episodes) ? episodes : []), [episodes]);

  // ✅ intenta usar el episodio más reciente como imagen OG (si existe)
  const latest = list?.[0];

  const seoTitle = "El Show de las Banqueras | Podcast de Banqueras y Juegos de Azar RD";
  const seoDesc =
    "El Show de las Banqueras: podcast dinámico sobre banqueras, bancas de loterías, resultados de loterías, casinos y empleo en República Dominicana. Episodios, entrevistas y oportunidades laborales.";

  const seoOgImage = (() => {
    const imgRaw = latest?.thumbnail_url || latest?.image || "";
    if (!String(imgRaw).trim()) return OG_IMAGE;

    return String(imgRaw).startsWith("http")
      ? String(imgRaw)
      : `${API_BASE}/${String(imgRaw).replace(/^\/+/, "")}`;
  })();

  // ✅ SEO sin Helmet (compatible con React 19)
  useEffect(() => {
    // helper: meta tags (name= o property=)
    const setMeta = (key, content, attr = "name") => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    // canonical link
    const setCanonical = (href) => {
      let link = document.querySelector(`link[rel="canonical"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    document.title = seoTitle;
    setCanonical(PAGE_URL);

    // Basic SEO
    setMeta("description", seoDesc);
    setMeta("keywords", KEYWORDS);

    // Open Graph (Facebook/WhatsApp)
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "El Show de las Banqueras", "property");
    setMeta("og:title", seoTitle, "property");
    setMeta("og:description", seoDesc, "property");
    setMeta("og:url", PAGE_URL, "property");
    setMeta("og:image", seoOgImage, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", seoTitle);
    setMeta("twitter:description", seoDesc);
    setMeta("twitter:image", seoOgImage);
  }, [seoTitle, seoDesc, seoOgImage]);

  return (
    <main className="container">
      <section className="hero">
        <div className="hero-inner">
          <div className="kicker">Bienvenidos</div>
          <h1 className="h1">
            El Show de las <span style={{ color: "var(--gold)" }}>Banqueras</span>
          </h1>
          <p className="p">
            🎙️ El Show de las Banqueras es un podcast dinámico, divertido y auténtico,
            dedicado a informar y comentar todo lo relacionado con el mundo de los juegos
            de azar en la República Dominicana.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
            <Link className="btn btn-blue" to="/empleo/busca">¿Buscas empleo? →</Link>
            <Link className="btn btn-pink" to="/empleo/contratando">Estamos contratando →</Link>
            <Link className="btn btn-ghost" to="/empleo">Ver Empleo</Link>
          </div>
        </div>
      </section>

      <section style={{ marginTop: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <h2 style={{ fontWeight: 900, marginBottom: 12 }}>🎙️ Episodios Recientes</h2>
          <button className="btn btn-ghost" onClick={fetchEpisodes} disabled={loadingEp}>
            {loadingEp ? "Cargando..." : "Actualizar"}
          </button>
        </div>

        {epErr && (
          <div className="card" style={{ marginBottom: 12, color: "#ffb86b" }}>
            ⚠️ {epErr}
          </div>
        )}

        {loadingEp ? (
          <div className="grid grid-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card" style={{ opacity: 0.7 }}>
                Cargando episodios...
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="card" style={{ opacity: 0.85 }}>
            Aún no hay episodios publicados.
          </div>
        ) : (
          <div className="grid grid-3">
            {list.map((ep) => {
              // ✅ soporta ambos: image (local) o thumbnail_url (server)
              const imgRaw = ep?.thumbnail_url || ep?.image || "";
              const img = String(imgRaw).trim()
                ? String(imgRaw).startsWith("http")
                  ? imgRaw
                  : `${API_BASE}/${String(imgRaw).replace(/^\/+/, "")}`
                : FALLBACK_IMG;

              const hasLink = Boolean(ep?.link?.trim());

              return (
                <div className="card" key={ep.id ?? ep.title}>
                  <img
                    src={img}
                    alt={ep?.title || "Episodio"}
                    onError={(e) => (e.currentTarget.src = FALLBACK_IMG)}
                    style={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 14,
                      marginBottom: 10,
                    }}
                    loading="lazy"
                  />

                  <h3 style={{ margin: "6px 0" }}>{ep?.title || "Episodio"}</h3>
                  <p className="p">{ep?.description || "Sin descripción."}</p>

                  {hasLink ? (
                    <a
                      href={ep.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-blue"
                      style={{ marginTop: 8 }}
                    >
                      Ver episodio →
                    </a>
                  ) : (
                    <button className="btn btn-ghost" style={{ marginTop: 8, opacity: 0.7 }} disabled>
                      Sin enlace
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section style={{ marginTop: 18 }} className="grid grid-3">
        <div className="card">
          <div style={{ fontWeight: 900 }}>🎙️ Episodios</div>
          <p className="p" style={{ marginBottom: 0 }}>Todos los lunes a las 9 de la noche tenemos nuevos episodios.</p>
        </div>
        <div className="card">
          <div style={{ fontWeight: 900 }}>⭐ Entrevistas</div>
          <p className="p" style={{ marginBottom: 0 }}>Invitados y historias reales.</p>
        </div>
        <div className="card">
          <div style={{ fontWeight: 900 }}>📰 Noticias</div>
          <p className="p" style={{ marginBottom: 0 }}>Últimas noticias relacionadas con el juego de azar en República Dominicana.</p>
        </div>
      </section>
    </main>
  );
}
